import { useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import * as Auth from "@aws-amplify/auth";
import { NAV_ITEM_HEIGHT } from "../../styles/layout";

/** Stored at login; the local part is the closest thing to a display name. */
const readSignedInEmail = () =>
  localStorage.getItem("user") ?? sessionStorage.getItem("user") ?? "";

const toDisplayName = (email: string) => {
  const local = email.split("@")[0];
  if (!local) return "Angemeldet";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const toInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

/**
 * Account control pinned to the foot of the sidebar. Shows the avatar
 * alone on the collapsed rail and opens its menu upward.
 */
export const UserMenu = ({ open }: { open: boolean }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const email = readSignedInEmail();
  const name = toDisplayName(email);

  const handleSignOut = async () => {
    setAnchorEl(null);
    localStorage.removeItem("user");
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("rememberMe");
    try {
      await Auth.signOut();
      navigate("/login");
    } catch {
      navigate("/error");
    }
  };

  const trigger = (
    <Box
      component="button"
      type="button"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      aria-label="Benutzermenü"
      aria-haspopup="menu"
      aria-expanded={!!anchorEl}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: open ? "100%" : NAV_ITEM_HEIGHT,
        mx: open ? 0 : "auto",
        p: open ? 0.75 : 0,
        justifyContent: open ? "flex-start" : "center",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
        backgroundColor: "transparent",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: theme.palette.surface.hover },
      })}
    >
      <Avatar
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          fontSize: "0.75rem",
          fontWeight: 600,
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        {toInitials(name)}
      </Avatar>

      {open && (
        <>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            {email && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {email}
              </Typography>
            )}
          </Box>
          <KeyboardArrowUpIcon
            sx={{ ml: "auto", flexShrink: 0, fontSize: "1.125rem", color: "text.secondary" }}
          />
        </>
      )}
    </Box>
  );

  return (
    <>
      {open ? (
        trigger
      ) : (
        <Tooltip title={name} placement="right">
          {trigger}
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        // Anchored at the foot of the sidebar, so the menu opens upward.
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 220, mb: 1 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {name}
          </Typography>
          {email && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {email}
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate("/settings");
          }}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ fontSize: "1.125rem" }} />
          </ListItemIcon>
          Einstellungen
        </MenuItem>
        <MenuItem onClick={handleSignOut} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <LogoutIcon sx={{ fontSize: "1.125rem", color: "error.main" }} />
          </ListItemIcon>
          Abmelden
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
